import React, { useCallback, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

import { SensitiveField } from '@/components/SensitiveField';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { cn } from '@/lib/utils.ts';
import { zipCodeRegex } from '@/lib/validation-regex.ts';
import { getState } from '@/lib/zipcode.ts';
import { CARRIER_TYPE, CARRIER_TYPE_ORDER, type CarrierType } from '@/types/insurance.type.ts';

const formId = 'insurance-discovery-form';
const noSelectionMessage = 'Please select at least one';
const formSchema = z
  .object({
    hasMedicaid: z.boolean(),
    hasMedicare: z.boolean(),
    hasPrivate: z.boolean(),
    zipCode: z.string().regex(zipCodeRegex, 'Zip code must be 5 digits'),
  })
  .refine((data) => data.hasMedicaid || data.hasMedicare || data.hasPrivate, {
    message: noSelectionMessage,
    path: ['hasMedicaid'],
  });
const carriersInForm: Set<CarrierType> = new Set([CARRIER_TYPE.MEDICARE, CARRIER_TYPE.MEDICAID]);

type FormData = z.infer<typeof formSchema>;

export type PreDiscoveryData = {
  zipCode: string;
  carrierTypes: Set<CarrierType>;
  hasPrivateInsurance: boolean;
};

function getCarrierTypes(data: FormData): Set<CarrierType> {
  const carrierTypes = new Set<CarrierType>();
  if (data.hasMedicaid) carrierTypes.add(CARRIER_TYPE.MEDICAID);
  if (data.hasMedicare) carrierTypes.add(CARRIER_TYPE.MEDICARE);
  return carrierTypes;
}

type Props = {
  onSubmit: (data: PreDiscoveryData) => void;
};

export function PreDiscoveryForm({ onSubmit }: Props) {
  const { insuranceZipCode, insuranceBuilder } = useSessionStore();

  // Derive checkbox defaults from stored preDiscoveryCarrierTypes
  const preDiscoveryCarrierTypes = insuranceBuilder.preDiscoveryCarrierTypes;
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      hasMedicaid: preDiscoveryCarrierTypes.includes(CARRIER_TYPE.MEDICAID),
      hasMedicare: preDiscoveryCarrierTypes.includes(CARRIER_TYPE.MEDICARE),
      hasPrivate: insuranceBuilder.hasPrivateInsurance,
      zipCode: insuranceZipCode,
    },
  });

  const zipCode = form.watch('zipCode');
  const state = useMemo(
    () => (!zipCode || zipCode.length < 5 ? null : getState(zipCode)),
    [zipCode]
  );

  const [showError, setShowError] = useState(false);
  const trackSubmitAttempt = useCallback(() => setShowError(true), []);
  const submitDiscoveryData = useCallback(
    (data: FormData) => {
      setShowError(true);
      onSubmit({
        zipCode: data.zipCode,
        carrierTypes: getCarrierTypes(data),
        hasPrivateInsurance: data.hasPrivate,
      });
    },
    [onSubmit]
  );

  // Zod through react form doesn't actually work, we just use the
  // `refine` to block and calculate the error here
  const watched = form.watch(['hasMedicaid', 'hasMedicare', 'hasPrivate']);
  const hasSelectionError = showError && watched.every((v) => v === false);

  const orderedControllers = CARRIER_TYPE_ORDER.filter((t) => carriersInForm.has(t)).map(
    (carrierType) => {
      const ControllerComponent = CONTROLLERS[carrierType];
      if (!ControllerComponent) return null;
      return <ControllerComponent key={carrierType} form={form} hasError={hasSelectionError} />;
    }
  );

  return (
    <form
      onSubmit={form.handleSubmit(submitDiscoveryData, trackSubmitAttempt)}
      id={formId}
      className="space-y-8 sm:space-y-12"
    >
      <FieldGroup className="space-y-7">
        <div className="space-y-3">
          <FieldSet className="space-y-4.5">
            <FieldLegend className="sr-only">Insurance plan selection</FieldLegend>
            <FieldGroup className="gap-4.5" data-slot="checkbox-group">
              {orderedControllers}
              <PrivateController form={form} hasError={hasSelectionError} />
            </FieldGroup>
          </FieldSet>
          {hasSelectionError && <FieldError errors={[{ message: noSelectionMessage }]} />}
        </div>

        <div className="min-h-22">
          <Controller
            name="zipCode"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${formId}-zipCode`}>Zip code (where insured)</FieldLabel>
                <Input
                  {...field}
                  id={`${formId}-zipCode`}
                  aria-invalid={fieldState.invalid}
                  maxLength={5}
                  inputMode="numeric"
                />
                {state && (
                  <FieldDescription className="text-sm font-medium text-[#2A9D90]">
                    Insured in <SensitiveField>{state}</SensitiveField>
                  </FieldDescription>
                )}
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </div>
      </FieldGroup>

      <Button type="submit" className="w-full sm:w-auto">
        Continue
      </Button>
    </form>
  );
}

function PrivateController({
  form,
  hasError,
}: {
  form: ReturnType<typeof useForm<FormData>>;
  hasError: boolean;
}) {
  return (
    <Controller
      name="hasPrivate"
      control={form.control}
      render={({ field }) => (
        <Field orientation="horizontal" className="items-start gap-3">
          <Checkbox
            id="private"
            checked={!!field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
            }}
            name={field.name}
            className={cn(hasError && 'border-rose-500')}
          />
          <div className="flex flex-col gap-1">
            <FieldLabel
              style={{ fontSize: '18px', fontWeight: '500' }}
              className={cn(hasError && 'text-rose-500')}
              htmlFor="private"
            >
              Commercial plan
            </FieldLabel>
            <FieldDescription>Employer or self-paid private plans</FieldDescription>
          </div>
        </Field>
      )}
    />
  );
}

const CONTROLLERS: Partial<
  Record<
    CarrierType,
    React.ComponentType<{ form: ReturnType<typeof useForm<FormData>>; hasError: boolean }>
  >
> = {
  [CARRIER_TYPE.MEDICAID]: MedicaidController,
  [CARRIER_TYPE.MEDICARE]: MedicareController,
};

function MedicaidController({
  form,
  hasError,
}: {
  form: ReturnType<typeof useForm<FormData>>;
  hasError: boolean;
}) {
  return (
    <Controller
      name="hasMedicaid"
      control={form.control}
      render={({ field }) => (
        <Field orientation="horizontal" className="items-start gap-3">
          <Checkbox
            id="medicaid"
            checked={!!field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
            }}
            name={field.name}
            className={cn(hasError && 'border-rose-500')}
          />
          <div className="flex flex-col gap-1">
            {/* TODO Figure out why tailwind doesn't work here and below */}
            <FieldLabel
              style={{ fontSize: '18px', fontWeight: '500' }}
              className={cn(hasError && 'text-rose-500')}
              htmlFor="medicaid"
            >
              Medicaid
            </FieldLabel>
            <FieldDescription>State sponsored or managed plans</FieldDescription>
          </div>
        </Field>
      )}
    />
  );
}

function MedicareController({
  form,
  hasError,
}: {
  form: ReturnType<typeof useForm<FormData>>;
  hasError: boolean;
}) {
  return (
    <Controller
      name="hasMedicare"
      control={form.control}
      render={({ field }) => (
        <Field orientation="horizontal" className="items-start gap-3">
          <Checkbox
            id="medicare"
            checked={!!field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
            }}
            name={field.name}
            className={cn(hasError && 'border-rose-500')}
          />
          <div className="flex flex-col gap-1">
            <FieldLabel
              style={{ fontSize: '18px', fontWeight: '500' }}
              className={cn(hasError && 'text-rose-500')}
              htmlFor="medicare"
            >
              Medicare
            </FieldLabel>
            <FieldDescription>Original or Advantage plans</FieldDescription>
          </div>
        </Field>
      )}
    />
  );
}
